"use client";

import { z } from "zod";
import { useToast } from "@/hooks/use-toast";
import { useTranslations } from "next-intl";
import { RequestCodeFormSchema } from "./schema";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormMessage } from "@/components/ui/form";
import Buttons from "@/components/core/Buttons";
import hooks from "@/hooks";
import { useRouter } from "next/navigation";
import PhoneInput from "react-phone-input-2";
import { ptBRCountries } from "@/consts/PtBRCountries";

type Request = z.infer<typeof RequestCodeFormSchema>;

const RequestCodeForm = () => {
  const t = useTranslations("Auth.RequestCodePage.form");

  const { toast } = useToast();
  const router = useRouter();

  const form = useForm<Request>({
    resolver: zodResolver(RequestCodeFormSchema),
    defaultValues: {
      phone: "",
    },
  });

  const requestCodeService = hooks.useRequestCodeByPhone({
    onSuccess: (data) => {
      handleRedirectToStep2();
    },
    onError: (err) => {
      toast({
        title: t("toasts.unexpectedError.resend.title"),
        description:
          err.message ?? t("toasts.unexpectedError.resend.description"),
        variant: "warning",
      });
    },
  });

  async function onSubmit(data: Request) {
    await requestCodeService.mutateAsync(data);
  }

  function handleRedirectToStep2() {
    router.push(`/recuperar-acesso/${form.getValues().phone}`);
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="md:p-8 w-full max-w-96 flex flex-col items-center gap-y-6"
      >
        <div className="flex flex-col w-full">
          <label htmlFor="whatsapp" className="text-sm font-semibold mb-1">
            {t("label.phone")}
          </label>
          <PhoneInput
            country={"br"}
            value={form.watch("phone")}
            onChange={(value) => {
              const numericValue = value.replace(/\D/g, "");
              form.setValue("phone", numericValue);
            }}
            enableSearch
            countryCodeEditable={false}
            localization={{ ...ptBRCountries }}
            inputStyle={{
              width: "100%",
              height: "40px",
              borderRadius: "9999px",
              border: "1px solid #e5e7eb",
              backgroundColor: "transparent",
              padding: "0.25rem 0.75rem",
              paddingLeft: "55px",
              fontSize: "1rem",
              boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
              transition: "all 0.2s",
              outline: "none",
            }}
            containerStyle={{ width: "100%" }}
            buttonStyle={{
              borderTopLeftRadius: "9999px",
              borderBottomLeftRadius: "9999px",
              width: "50px",
              border: "1px solid rgb(229, 231, 235)",
              paddingLeft: "6px",
            }}
            dropdownStyle={{
              backgroundColor: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
            }}
            inputProps={{
              name: "whatsapp",
              placeholder: t("placeholder.whatsapp"),
            }}
          />
          {form.formState.errors.phone && (
            <FormMessage className="mt-2">
              {form.formState.errors.phone.message}
            </FormMessage>
          )}
        </div>

        <Buttons.DefaultButton
          className="py-6"
          title={t("button")}
          type="submit"
          onClick={form.handleSubmit(onSubmit)}
          isLoading={requestCodeService.isPending}
        />
      </form>
    </Form>
  );
};

export default RequestCodeForm;
